import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  FileButton,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconBolt, IconUpload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import type {
  HeaderInput,
  CarouselSendCardInput,
} from '../../../../api/src/modules/campaigns/schema';
import { CarouselSendEditor, detectCarouselCards } from '../templates/CarouselSendEditor';
import {
  extractBodyText,
  extractButtons,
  extractHeaderFormat,
  extractId,
  hasComponent,
} from '../../lib/template-introspection';
import { buildPreviewTemplate } from '../../lib/template-preview-builder';
import { TemplatePreview } from '../TemplatePreview';
import { TokenTextInput, buildContactTokenOptions } from '../flow-builder/TokenTextInput';

type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | 'GIF';

/**
 * Send a Meta-approved WhatsApp template to a single contact — the 1:1 chat
 * equivalent of the campaign wizard's template step. Previously chat could
 * only send plain body-variable templates (or nothing at all outside the
 * 24h window, despite the composer's placeholder text promising otherwise);
 * this brings header media/text, a dynamic URL-button suffix, a bound Flow,
 * a real coupon code, and an LTO expiration to 1:1 sends too.
 */
export function SendTemplateModal({
  opened,
  onClose,
  contactId,
  onSent,
}: {
  opened: boolean;
  onClose: () => void;
  contactId: number;
  onSent?: () => void;
}) {
  const templates = trpc.whatsapp.templates.list.useQuery(undefined, { enabled: opened });
  const forms = trpc.forms.list.useQuery(undefined, { enabled: opened });
  const customFields = trpc.contacts.customFields.list.useQuery(undefined, { enabled: opened });
  const uploadMedia = trpc.whatsapp.templates.uploadSendMedia.useMutation();

  // Insertable dynamic-data tokens: the contact's fields + every custom field.
  // The backend renders these against this recipient at send time.
  const tokenOptions = useMemo(
    () => buildContactTokenOptions(customFields.data ?? []),
    [customFields.data],
  );

  const [templateId, setTemplateId] = useState<number | null>(null);
  const [bodyVariables, setBodyVariables] = useState<string[]>([]);
  const [header, setHeader] = useState<HeaderInput | null>(null);
  const [urlButtonText, setUrlButtonText] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [limitedTimeOfferExpiresAt, setLimitedTimeOfferExpiresAt] = useState<Date | null>(null);
  const [flowUid, setFlowUid] = useState<string | null>(null);
  const [carousel, setCarousel] = useState<CarouselSendCardInput[]>([]);
  // Local-only (never sent) — a blob URL for the live preview below, same
  // pattern FlowTemplatePanel uses for its own live preview.
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState<string | null>(null);

  const reset = () => {
    setTemplateId(null);
    setBodyVariables([]);
    setHeader(null);
    setUrlButtonText('');
    setCouponCode('');
    setLimitedTimeOfferExpiresAt(null);
    setFlowUid(null);
    setCarousel([]);
    setHeaderPreviewUrl(null);
  };

  const send = trpc.chat.sendTemplate.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Template sent', color: 'green' });
      reset();
      onClose();
      onSent?.();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const selectedTemplate = useMemo(() => {
    if (!templateId) return null;
    return (templates.data ?? []).find((t) => extractId(t) === templateId) ?? null;
  }, [templateId, templates.data]);

  const bodyText = extractBodyText(selectedTemplate);
  const detectedVars = useMemo(() => {
    if (!bodyText) return [] as number[];
    const out = new Set<number>();
    for (const m of bodyText.matchAll(/\{\{(\d+)\}\}/g)) out.add(Number(m[1]));
    return [...out].sort((a, b) => a - b);
  }, [bodyText]);

  useEffect(() => {
    setBodyVariables((prev) => {
      const next = prev.slice(0, detectedVars.length);
      while (next.length < detectedVars.length) next.push('');
      return next;
    });
  }, [detectedVars.length]);

  const headerFormat = (extractHeaderFormat(selectedTemplate) ?? 'NONE') as HeaderFormat;
  useEffect(() => {
    setHeaderPreviewUrl(null);
    if (headerFormat === 'NONE' || headerFormat === 'GIF') setHeader(null);
    else if (headerFormat === 'TEXT') setHeader({ kind: 'text', variables: [] });
    else if (headerFormat === 'IMAGE') setHeader({ kind: 'image' });
    else if (headerFormat === 'VIDEO') setHeader({ kind: 'video' });
    else if (headerFormat === 'DOCUMENT') setHeader({ kind: 'document' });
    else if (headerFormat === 'LOCATION')
      setHeader({ kind: 'location', latitude: 0, longitude: 0 });
  }, [headerFormat]);

  const carouselCards = useMemo(() => detectCarouselCards(selectedTemplate), [selectedTemplate]);

  const previewTemplate = useMemo(() => {
    if (!selectedTemplate || carouselCards) return null;
    return buildPreviewTemplate(
      selectedTemplate as { components?: unknown[]; category?: string | null },
      {
        bodyVariables,
        headerMediaUrl: headerPreviewUrl,
        headerText: header?.kind === 'text' ? (header.variables[0] ?? '') : undefined,
        limitedTimeOfferExpiresAtMs: limitedTimeOfferExpiresAt?.getTime(),
      },
    );
  }, [
    selectedTemplate,
    carouselCards,
    bodyVariables,
    headerPreviewUrl,
    header,
    limitedTimeOfferExpiresAt,
  ]);

  const buttons = extractButtons(selectedTemplate);
  const urlButtonIndex = buttons.findIndex(
    (b) => b.type === 'URL' && (b.url ?? '').includes('{{1}}'),
  );
  const flowButtonIndex = buttons.findIndex((b) => b.type === 'FLOW');
  const copyCodeButtonIndex = buttons.findIndex((b) => b.type === 'COPY_CODE');
  const hasLimitedTimeOffer = hasComponent(selectedTemplate, 'LIMITED_TIME_OFFER');
  useEffect(() => {
    setCarousel([]);
  }, [carouselCards]);

  const handleHeaderFile = async (file: File | null) => {
    if (!file || !header) return;
    setHeaderPreviewUrl(URL.createObjectURL(file));
    const buf = await file.arrayBuffer();
    const base64 = bufferToBase64(buf);
    uploadMedia.mutate(
      { base64, mimeType: file.type, filename: file.name },
      {
        onSuccess: (r) => {
          if (header.kind === 'image') setHeader({ kind: 'image', mediaId: r.mediaId });
          else if (header.kind === 'video') setHeader({ kind: 'video', mediaId: r.mediaId });
          else if (header.kind === 'document')
            setHeader({ kind: 'document', mediaId: r.mediaId, filename: file.name });
          notifications.show({ message: 'Media uploaded', color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };

  const carouselComplete =
    !carouselCards ||
    (carousel.length === carouselCards.length &&
      carousel.every(
        (c, i) =>
          !!(c.header as { mediaId?: string } | undefined)?.mediaId &&
          (c.bodyVariables ?? []).length === (carouselCards[i]?.bodyVarCount ?? 0) &&
          (c.bodyVariables ?? []).every((v) => v.trim().length > 0),
      ));

  const canSubmit =
    !!selectedTemplate &&
    detectedVars.every((_, i) => (bodyVariables[i] ?? '').trim().length > 0) &&
    (headerFormat !== 'IMAGE' && headerFormat !== 'VIDEO' && headerFormat !== 'DOCUMENT'
      ? true
      : header?.kind !== 'location' && !!(header as { mediaId?: string })?.mediaId) &&
    carouselComplete;

  const submit = () => {
    if (!selectedTemplate) return;
    send.mutate({
      contactId,
      templateName: (selectedTemplate as { name: string }).name,
      languageCode: (selectedTemplate as { language: string }).language,
      bodyVariables: bodyVariables.length > 0 ? bodyVariables : undefined,
      header: header ?? undefined,
      ...(urlButtonIndex >= 0 && urlButtonText ? { urlButtonText, urlButtonIndex } : {}),
      ...(flowButtonIndex >= 0 && flowUid ? { flowUid, flowButtonIndex } : {}),
      ...(copyCodeButtonIndex >= 0 && couponCode
        ? { couponCode, couponCodeButtonIndex: copyCodeButtonIndex }
        : {}),
      ...(hasLimitedTimeOffer && limitedTimeOfferExpiresAt
        ? { limitedTimeOfferExpiresAt: limitedTimeOfferExpiresAt.toISOString() }
        : {}),
      ...(carouselCards ? { carousel } : {}),
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Send a template"
      size="lg"
    >
      <Stack gap="sm">
        {templates.isPending ? (
          <Skeleton height={48} />
        ) : (
          <Select
            label="Approved template"
            placeholder="Choose a template"
            searchable
            data={(templates.data ?? [])
              .filter((t) => (t as { status?: string }).status === 'APPROVED')
              .map((t) => ({
                value: String(extractId(t)),
                label: `${(t as { name: string }).name} (${(t as { language: string }).language})`,
              }))}
            value={templateId ? String(templateId) : null}
            onChange={(v) => setTemplateId(v ? Number(v) : null)}
          />
        )}

        {detectedVars.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Body variables ({detectedVars.length})
            </Text>
            {detectedVars.map((n, i) => (
              <TokenTextInput
                key={n}
                label={`{{${n}}}`}
                options={tokenOptions}
                value={bodyVariables[i] ?? ''}
                onChange={(value) =>
                  setBodyVariables((prev) => {
                    const next = [...prev];
                    next[i] = value;
                    return next;
                  })
                }
              />
            ))}
          </Stack>
        )}

        {headerFormat === 'TEXT' && (
          <TokenTextInput
            label="Header text"
            options={tokenOptions}
            value={header?.kind === 'text' ? (header.variables[0] ?? '') : ''}
            onChange={(value) => setHeader({ kind: 'text', variables: [value] })}
          />
        )}
        {(headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') && (
          <Group>
            <FileButton
              onChange={handleHeaderFile}
              accept={
                headerFormat === 'IMAGE'
                  ? 'image/*'
                  : headerFormat === 'VIDEO'
                    ? 'video/*'
                    : 'application/pdf'
              }
            >
              {(props) => (
                <Button
                  {...props}
                  variant="light"
                  leftSection={<IconUpload size={14} />}
                  loading={uploadMedia.isPending}
                >
                  Upload {headerFormat.toLowerCase()}
                </Button>
              )}
            </FileButton>
            {!!(header as { mediaId?: string })?.mediaId && (
              <Badge color="green" variant="light">
                Uploaded ✓
              </Badge>
            )}
          </Group>
        )}

        {urlButtonIndex >= 0 && (
          <TokenTextInput
            label="Link for the URL button"
            description="Fills the button's {{1}} — e.g. a payment or tracking link for this contact."
            options={tokenOptions}
            value={urlButtonText}
            onChange={setUrlButtonText}
          />
        )}

        {flowButtonIndex >= 0 && (
          <Select
            label="Attach a form"
            description="Fills the template's Flow button so submissions link back to this contact."
            placeholder={forms.isPending ? 'Loading…' : 'No form'}
            clearable
            searchable
            data={(forms.data ?? [])
              .filter((f) => f.status === 'PUBLISHED')
              .map((f) => ({ value: f.uid, label: f.name }))}
            value={flowUid}
            onChange={setFlowUid}
          />
        )}

        {copyCodeButtonIndex >= 0 && (
          <TextInput
            label="Coupon / offer code"
            maxLength={20}
            value={couponCode}
            onChange={(e) => setCouponCode(e.currentTarget.value)}
          />
        )}

        {carouselCards && <CarouselSendEditor cards={carouselCards} onChange={setCarousel} />}

        {hasLimitedTimeOffer && (
          <DateTimePicker
            label="Offer expires at"
            clearable
            minDate={new Date()}
            value={limitedTimeOfferExpiresAt}
            onChange={(v) => setLimitedTimeOfferExpiresAt(v ? new Date(v) : null)}
          />
        )}

        {previewTemplate && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Live preview
            </Text>
            <TemplatePreview template={previewTemplate} />
          </Stack>
        )}

        {selectedTemplate && !canSubmit && (
          <Alert color="yellow" icon={<IconBolt size={14} />}>
            Fill in every body variable
            {headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT'
              ? ' and upload the header media'
              : ''}
            {carouselCards ? ' and complete every carousel card (media + body text)' : ''} before
            sending.
          </Alert>
        )}

        <Group justify="end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} loading={send.isPending} onClick={submit}>
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── helpers ──

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
